/**
 * Persistence + forward-compat reconciliation for NavConfig.
 *
 * Phase 1: localStorage only. Phase 3 will swap `loadNavConfig`/`saveNavConfig`
 * to the `/api/nav-config` endpoint (with localStorage as offline cache) WITHOUT
 * touching any call site — selectors, mutations, context and components only
 * ever go through this module.
 *
 * `reconcileWithCatalog` keeps a stored config in sync with the page catalog:
 * pages no longer in the catalog are dropped; catalog pages missing from a
 * perspective go to their default group (NC-1622), or to the tray when the
 * default has no place for them. This makes adding/removing a page a pure-data
 * change — old stored configs self-heal on load.
 */
import { NavConfig, NavPage, PageId, Perspective, PERSPECTIVES } from './navTypes';
import { DEFAULT_NAV_CONFIG } from './defaultNavConfig';
import { PAGE_CATALOG, ALL_PAGE_IDS } from './pageCatalog';
import { getNavConfigFromApi, putNavConfigToApi } from '../services/navConfigApi';

const STORAGE_KEY = 'nc.navConfig.v1';
const PERSPECTIVE_KEY = 'nc.navPerspective.v1';

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

/**
 * NC-1622: where a catalog page that the stored config has never seen belongs.
 *
 * Returns the index of the default group (by stable id) that places `id` in this
 * perspective, plus the insert position inside the stored group: right after the
 * last page that precedes `id` in the default order, else at the front.
 * `null` when the default places the page nowhere or the stored config no
 * longer has that group — the page then goes to the tray as before.
 */
function defaultSlot(
  perspective: Perspective,
  id: PageId,
  groups: { id: string; pages: NavPage[] }[]
): { groupIndex: number; position: number } | null {
  const defaultGroup = DEFAULT_NAV_CONFIG[perspective].groups.find((g) =>
    g.pages.some((p) => p.id === id)
  );
  if (!defaultGroup) return null;
  const groupIndex = groups.findIndex((g) => g.id === defaultGroup.id);
  if (groupIndex < 0) return null;

  const defaultOrder = defaultGroup.pages.map((p) => p.id);
  const predecessors = new Set(defaultOrder.slice(0, defaultOrder.indexOf(id)));
  let position = 0;
  groups[groupIndex].pages.forEach((p, i) => {
    if (predecessors.has(p.id)) position = i + 1;
  });
  return { groupIndex, position };
}

/**
 * Drop orphan/duplicate pages; place catalog pages the stored config has never
 * seen.
 *
 * NC-1622: a page added to the catalog AFTER the config was saved used to land in
 * the tray — which the live navigation never shows. The prod config was saved on
 * 2026-06-19, so Kostenstellen, FinOps, Schulungskalender, Neues Image and Hilfe
 * were unreachable from the sidebar. Such pages now go to their default group
 * (when the stored config still has it). A page the admin deliberately put in the
 * tray is present in `src.tray` and therefore stays there.
 */
export function reconcileWithCatalog(cfg: NavConfig): NavConfig {
  const result = {} as NavConfig;
  for (const perspective of PERSPECTIVES) {
    const src = cfg[perspective] ?? DEFAULT_NAV_CONFIG[perspective];
    const seen = new Set<PageId>();
    const keep = (p: NavPage, forceVisible = false): NavPage | null => {
      if (!PAGE_CATALOG[p.id] || seen.has(p.id)) return null;
      seen.add(p.id);
      return { id: p.id, hidden: forceVisible ? false : !!p.hidden };
    };

    const groups = (src.groups ?? []).map((g) => ({
      id: g.id,
      name: g.name,
      pages: (g.pages ?? []).map((p) => keep(p)).filter((p): p is NavPage => p !== null),
    }));

    const tray: NavPage[] = (src.tray ?? [])
      .map((p) => keep(p, true))
      .filter((p): p is NavPage => p !== null);

    // Any catalog page not placed anywhere → its default group, else the tray.
    for (const id of ALL_PAGE_IDS) {
      if (!seen.has(id)) {
        seen.add(id);
        const slot = defaultSlot(perspective, id, groups);
        if (slot) {
          groups[slot.groupIndex].pages.splice(slot.position, 0, { id, hidden: false });
        } else {
          tray.push({ id, hidden: false });
        }
      }
    }

    result[perspective] = { groups, tray };
  }
  return result;
}

/** Load + reconcile the nav config (localStorage now; API in Phase 3). */
export function loadNavConfig(): NavConfig {
  try {
    const raw = hasLocalStorage() ? localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return reconcileWithCatalog(DEFAULT_NAV_CONFIG);
    return reconcileWithCatalog(JSON.parse(raw) as NavConfig);
  } catch (err) {
    console.warn('[navConfigStore] failed to load nav config, using default:', err);
    return reconcileWithCatalog(DEFAULT_NAV_CONFIG);
  }
}

/** localStorage-only write (no server round-trip). */
function cacheNavConfigLocal(cfg: NavConfig): void {
  try {
    if (hasLocalStorage()) localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
  } catch (err) {
    console.warn('[navConfigStore] failed to cache nav config:', err);
  }
}

/**
 * Persist a config: write the localStorage cache immediately (instant, offline-
 * safe) AND best-effort PUT to /api/nav-config. localStorage stays the source of
 * truth until the server container is provisioned (run 074); the PUT is only ever
 * triggered from the admin editor.
 */
export function saveNavConfig(cfg: NavConfig): void {
  cacheNavConfigLocal(cfg);
  void putNavConfigToApi(cfg);
}

/**
 * Fetch the server config and reconcile it against the catalog. Returns null
 * when the server has no config yet (or is unreachable) so the caller keeps the
 * localStorage cache / bundled default. Updates the local cache on success.
 */
export async function refreshNavConfigFromApi(): Promise<NavConfig | null> {
  const remote = await getNavConfigFromApi();
  if (!remote) return null;
  const reconciled = reconcileWithCatalog(remote);
  cacheNavConfigLocal(reconciled);
  return reconciled;
}

/** Persisted UI choice of the active perspective (null = not yet chosen). */
export function loadStoredPerspective(): Perspective | null {
  try {
    const raw = hasLocalStorage() ? localStorage.getItem(PERSPECTIVE_KEY) : null;
    return raw === 'tutor' || raw === 'admin' || raw === 'betrieb' ? raw : null;
  } catch {
    return null;
  }
}

export function saveStoredPerspective(perspective: Perspective): void {
  try {
    if (hasLocalStorage()) localStorage.setItem(PERSPECTIVE_KEY, perspective);
  } catch {
    /* ignore */
  }
}
